'use strict';

/**
 * Cookie value serializer — RFC 6265-compliant encode/decode utilities.
 *
 * RFC 6265 (https://www.rfc-editor.org/rfc/rfc6265) defines the allowed character
 * set for cookie values as `cookie-octet`:
 *
 *   cookie-octet = %x21 / %x23-2B / %x2D-3A / %x3C-5B / %x5D-7E
 *
 * Characters explicitly forbidden include:
 *   - %x20  SPACE
 *   - %x22  double-quote "
 *   - %x2C  comma ,
 *   - %x3B  semicolon ;
 *   - %x5C  backslash \
 *
 * Starting in Salesforce B2C Commerce 26.5 the platform enforces RFC 6265 strictly:
 * any forbidden character in a cookie value causes it to be silently dropped.
 *
 * Strategy: percent-encode on write (`encodeURIComponent`), decode on read
 * (`decodeURIComponent`). The `%` character (%x25) is a valid cookie-octet.
 * `decodeURIComponent` is a no-op on legacy un-encoded values, so reads are
 * automatically backward-compatible with cookies written before this change.
 */

/**
 * Encodes a cookie value so it only contains RFC 6265-allowed characters.
 * @param {string} value - Raw cookie value (may contain commas, quotes, spaces, etc.)
 * @returns {string} Percent-encoded string safe to use as a cookie value.
 */
function encodeCookieValue(value) {
    return encodeURIComponent(String(value));
}

/**
 * Decodes a cookie value encoded with {@link encodeCookieValue}.
 * No-op on legacy un-encoded values — backward-compatible.
 * @param {string} value - Percent-encoded cookie value as read from the request.
 * @returns {string} Original decoded string.
 */
function decodeCookieValue(value) {
    return decodeURIComponent(value);
}

/**
 * Custom codecs for cookies whose value format requires special handling.
 */
var customCodecs = {
    /**
     * adLocale cookie codec.
     * Kept here to avoid a circular dependency between siteSelectorHelper and
     * countrySelectorHelper.
     *
     * encode: 'country_language' — underscore is RFC 6265-safe.
     * decode: backward-compatible with legacy formats (see below).
     *
     * ## Legacy format and the double-quote problem
     *
     * Before Salesforce 26.5, the cookie was set as 'country,language' (comma
     * separator). The comma is a forbidden character in RFC 6265 cookie-octets,
     * but the spec also allows wrapping the entire value in double-quotes
     * (DQUOTE) as an escape mechanism. Salesforce 26.4 (and some SFRA helpers)
     * used this: the Set-Cookie header was emitted as:
     *
     *   Set-Cookie: adLocale="es,en"
     *
     * The browser stores and returns the value *including* the surrounding
     * double-quotes — they are not stripped by the browser or by
     * document.cookie. Reading the cookie therefore yields '"es,en"' or
     * '"es_ES"' (after the separator was changed to underscore but the
     * quoting habit persisted). The leading '"' made country codes appear as
     * 3-character strings (e.g. '"es') instead of 2.
     *
     * The `replace(/^"|"$/g, '')` strips those surrounding quotes before
     * splitting, making decode robust for all legacy values seen in the wild.
     */
    adLocale: {
        /**
         * @param {string} country - e.g. 'es'
         * @param {string} language - e.g. 'en'
         * @returns {string} 'country_language'
         */
        encode: function (country, language) {
            return country + '_' + language;
        },
        /**
         * @param {string} value - Raw adLocale cookie value.
         * @returns {string[]} [country, language]
         */
        decode: function (value) {
            var clean = value.replace(/^"|"$/g, '');
            var separator = clean.indexOf(',') !== -1 ? ',' : '_';
            return clean.split(separator);
        }
    }
};

module.exports = {
    encodeCookieValue: encodeCookieValue,
    decodeCookieValue: decodeCookieValue,
    customCodecs: customCodecs
};
